import React from 'react';
import styled from '@emotion/styled';
import { breakpoints, colors, css, respondFrom } from '@styles';
import Accordion from '@components/common/Accordion';
import BorderLine from '@components/common/BorderLine';
import { ConfiguratorSettings } from '@content/types/configuratorSettings';
import * as utils from '@utils';
import { LeasingInfo } from '@store/content/types';
import { PricesSummaryType } from '@content/types/prices';

const StyledShoppingSummaryPrices = styled.div`
    ${respondFrom(
    breakpoints.lg,
    css`
       margin-bottom: 30px;
    `
)};
`;

const StyledTrigger = styled.div`
    display: flex;
    justify-content: space-between;
`;

const StyledWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding-bottom: 35px;
    border-bottom: 1px solid #848484;
    margin-top: 35px;
    &.align-to-top {
        align-items: flex-start;
    }
    ${respondFrom(
    breakpoints.lg,
    css`
        padding-right: 25px;
        padding-bottom: 0;
        border: 0;
        margin-top: 20px;
    `
)};
`;

const StyledName = styled.div`
    font-size: 13px;
    font-weight: normal;
    color: ${colors.grayLight};
`;

const StyledCurrency = styled.div`
    font-size: 13px;
    font-weight: normal;
    color: ${colors.grayLight};
    span {
        display: block;
        white-space: nowrap;
        margin-left: 10px;
        text-align: right;
        &:first-of-type {
            font-weight: bold;
        }
    }
`;

const StyledCurrencyGross = styled.div`
    font-size: 11px;
    font-weight: normal;
    color: ${colors.grayLight};
    text-align: right;
`;

const StyledCurrencyTotalGross = styled(StyledCurrency)`
    margin-top: -30px;
    margin-left: auto;
    ${respondFrom(
    breakpoints.lg,
    css`
        margin-top: -15px;
    `
)};
`;

interface Props {
    pageSettings: ConfiguratorSettings;
    leasingInfo: LeasingInfo;
    prices: PricesSummaryType
    paymentType: string;
    productsTypes: string;
}

const ShoppingSummaryPrices = ({ pageSettings, leasingInfo, prices, paymentType, productsTypes }: Props) => {
    const pPole = pageSettings.field_proste_pola;
    const { monthly, oneOff } = prices;
    const cashAndLeasingPayments = ['cash', 'leasing'];
    const productsType = productsTypes === 'terminal'
      ? pPole[190]
      : productsTypes === 'tablet'
        ? pPole[191]
        : `${pPole[190]} / ${pPole[191]}`;
    const leaseText = `${pPole[186].charAt(0).toUpperCase() + pPole[186].slice(1)} ${productsType}`;
    const monthlySummaryLabel = pPole ? cashAndLeasingPayments.includes(paymentType) ? leaseText : pPole[147] : ''
    const monthlySummaryLabelDiscount = pPole ? cashAndLeasingPayments.includes(paymentType) ? pPole[187] : pPole[148] : ''

    return (
        <StyledShoppingSummaryPrices>
            <Accordion
                trigger={
                    <StyledTrigger>
                        <span>{pPole ? pPole[143] : ''}</span>
                        {
                            (monthly && !!monthly.total && monthly.total.net >= 0) &&
                            <span>{`${utils.formatCurrency(monthly.total.net.toFixed(2))} ${pPole ? pPole[14] : ''}`}</span>
                        }
                    </StyledTrigger>
                }
                triggerWhenOpen={pPole ? pPole[144] : ''}
                triggerDisabled={!((monthly && monthly.total.net > 0) || (monthly && monthly.productsLeaseReducedDuration && monthly.productsLeaseReducedDuration > 0))}
            >
                {
                    (monthly && !!monthly.total && !!monthly.total.gross) &&
                    <StyledWrapper>
                        <StyledCurrencyTotalGross>
                            {`${utils.formatCurrency(monthly.total.gross.toFixed(2))} ${pPole ? pPole[15] : ''}`}
                        </StyledCurrencyTotalGross>
                    </StyledWrapper>
                }
                {
                    (monthly && !!monthly.productsLeaseReducedDuration && !!monthly.productsLeaseReduced && !!monthly.productsLeaseAfterReduced) &&
                    <StyledWrapper className="align-to-top">
                        <StyledName>
                            {monthlySummaryLabelDiscount}
                        </StyledName>
                        <StyledCurrency>
                            <span>{`${utils.formatCurrency(monthly.productsLeaseReduced.net.toFixed(2))} ${pPole ? pPole[14] : ''} ${pPole ? pPole[156] : ''} / ${monthly.productsLeaseReducedDuration} ${pPole ? pPole[156] : ''}`}</span>
                            <span>{`${utils.formatCurrency(monthly.productsLeaseAfterReduced.net.toFixed(2))} ${pPole ? pPole[14] : ''} ${pPole ? pPole[156] : ''} / ${pPole ? pPole[160] : ''} ${monthly.productsLeaseReducedDuration} ${pPole ? pPole[159] : ''}`}</span>
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (monthly && !!monthly.productsLease && !!monthly.productsLease.net) &&
                    <StyledWrapper>
                        <StyledName>
                            {monthlySummaryLabel}
                        </StyledName>
                        <StyledCurrency>
                            {`${utils.formatCurrency(monthly.productsLease.net.toFixed(2))} ${pPole ? pPole[14] : ''} ${pPole ? pPole[156] : ''} / ${monthly.productsLeaseDuration} ${pPole ? pPole[156] : ''}`}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (monthly && !!monthly.leasingInstallment && !!monthly.leasingInstallment.net) &&
                    <StyledWrapper className="align-to-top">
                        <StyledName>{pPole ? pPole[163] : ''}</StyledName>
                        <StyledCurrency>
                            {`${leasingInfo.installmentquantity} x ${utils.formatCurrency(monthly.leasingInstallment.net.toFixed(2))} ${pPole ? pPole[14] : ''}`}
                            {
                                !!monthly.leasingInstallment.gross &&
                                <StyledCurrencyGross>
                                    {`${leasingInfo.installmentquantity} x ${utils.formatCurrency(monthly.leasingInstallment.gross.toFixed(2))} ${pPole ? pPole[15] : ''}`}
                                </StyledCurrencyGross>
                            }
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (monthly && !!monthly.tariff && !!monthly.tariff.net) &&
                    <StyledWrapper>
                        <StyledName>
                            {pPole ? pPole[149] : ''} <br />
                            {pPole ? pPole[150] : ''}
                        </StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(monthly.tariff.net)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
            </Accordion>
            <BorderLine color={colors.summaryBorder} />
            <Accordion
                trigger={
                    <StyledTrigger>
                        <span>{pPole ? pPole[145] : ''}</span>
                        {
                            (oneOff && !!oneOff.total && oneOff.total.net >= 0) &&
                            <span>{`${utils.formatCurrency(oneOff.total.net.toFixed(2))} ${pPole ? pPole[14] : ''}`}</span>
                        }
                    </StyledTrigger>
                }
                triggerWhenOpen={pPole ? pPole[146] : ''}
                triggerDisabled={!(oneOff && oneOff.total.net > 0)}
            >
                {
                    (oneOff && !!oneOff.total.gross) &&
                    <StyledWrapper>
                        <StyledCurrencyTotalGross>
                            {`${utils.formatCurrency(oneOff.total.gross.toFixed(2))} ${pPole ? pPole[15] : ''}`}
                        </StyledCurrencyTotalGross>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.productsCashReduced && !!oneOff.productsCashReduced.net) &&
                    <StyledWrapper>
                        <StyledName>
                            {pPole ? pPole[148] : ''}
                        </StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.productsCashReduced.net)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.productsCash && !!oneOff.productsCash.net) &&
                    <StyledWrapper>
                        <StyledName>
                            {pPole ? pPole[147] : ''}
                        </StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.productsCash.net)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.productsLeaseActivation && !!oneOff.productsLeaseActivation.net) &&
                    <StyledWrapper>
                        <StyledName>{pPole ? paymentType === 'cash' ? pPole[184] : pPole[152] : ''}</StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.productsLeaseActivation.net)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.productsAccessories && !!oneOff.productsAccessories.net) &&
                    <StyledWrapper>
                        <StyledName>{pPole ? pPole[153] : ''}</StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.productsAccessories.net)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.productsFiscalization && !!oneOff.productsFiscalization.net) &&
                    <StyledWrapper>
                        <StyledName>{pPole ? pPole[164] : ''}</StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.productsFiscalization.net)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.leasingDeposit && !!oneOff.leasingDeposit.net) &&
                    <StyledWrapper>
                        <StyledName>{pPole ? pPole[86] : ''}</StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.leasingDeposit.net.toFixed(2))} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
                {
                    (oneOff && !!oneOff.discountCash && !!oneOff.discountCash.net) &&
                    <StyledWrapper>
                        <StyledName>{pPole ? pPole[155] : ''}</StyledName>
                        <StyledCurrency>
                            {utils.formatCurrency(oneOff.discountCash.net * -1)} {pPole ? pPole[14] : ''}
                        </StyledCurrency>
                    </StyledWrapper>
                }
            </Accordion>
            <BorderLine color={colors.summaryBorder} />
        </StyledShoppingSummaryPrices>
    )
}

export default ShoppingSummaryPrices;
